import { useEffect, useState } from "react";
import { X, BookOpen } from "lucide-react";
import { API } from "@/App";
import { SURAH_LIST } from "@/data/quranMeta";

/**
 * TranslationDialog — full-screen readable translation overlay triggered by
 * the long-press action menu. Shows the Arabic ayah above the English
 * translation in large readable typography.
 *
 * On open, if the parent didn't already have a translation cached for the
 * requested (surah, ayah), we fetch it lazily so Page Mode works across
 * multiple surahs on a single spread.
 */
export default function TranslationDialog({
  open,
  onClose,
  surahNumber,
  ayahNumber,
  initialArabic = "",
  initialTranslation = "",
  translationEdition = "en.sahih",
}) {
  const [arabic, setArabic] = useState(initialArabic);
  const [english, setEnglish] = useState(initialTranslation);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setArabic(initialArabic);
    setEnglish(initialTranslation);
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    // If we already have at least the translation text, skip the fetch.
    if (initialTranslation && initialArabic) {
      return () => document.removeEventListener("keydown", onKey);
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [arRes, enRes] = await Promise.all([
          !initialArabic
            ? fetch(`${API}/quran/surah/${surahNumber}?edition=quran-uthmani`)
            : Promise.resolve(null),
          !initialTranslation
            ? fetch(
                `${API}/quran/surah/${surahNumber}?edition=${translationEdition}`,
              )
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (arRes && arRes.ok) {
          const d = await arRes.json();
          const a = d?.ayahs?.find((x) => x.numberInSurah === ayahNumber);
          if (a?.text) setArabic(a.text);
        }
        if (enRes && enRes.ok) {
          const d = await enRes.json();
          const a = d?.ayahs?.find((x) => x.numberInSurah === ayahNumber);
          if (a?.text) setEnglish(a.text);
        }
      } catch (err) {
        console.error("translation lazy-fetch failed:", err);
        /* fall through to whatever we have */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, surahNumber, ayahNumber, initialArabic, initialTranslation, translationEdition, onClose]);

  if (!open) return null;

  const surahMeta = SURAH_LIST.find((s) => s.number === surahNumber);

  return (
    <div
      data-testid="translation-dialog"
      onClick={onClose}
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-outfit"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-islamic-blue-900 border border-gold-400/30 shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 bg-islamic-blue-900 border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gold-400/15 flex items-center justify-center shrink-0">
              <BookOpen className="w-4 h-4 text-gold-400" />
            </div>
            <div className="min-w-0">
              <p className="font-cinzel text-gold-400 text-sm truncate">
                {surahMeta?.englishName || `Surah ${surahNumber}`}
              </p>
              <p className="text-slate-400 text-xs">
                Ayah {surahNumber}:{ayahNumber}
                {surahMeta?.englishNameTranslation
                  ? ` · ${surahMeta.englishNameTranslation}`
                  : ""}
              </p>
            </div>
          </div>
          <button
            data-testid="translation-dialog-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-islamic-blue-800 hover:bg-islamic-blue-700 text-slate-300 hover:text-gold-400 flex items-center justify-center transition-colors shrink-0"
            aria-label="Close translation"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-8 space-y-6">
          {arabic ? (
            <p
              data-testid="translation-dialog-arabic"
              dir="rtl"
              className="font-noto-naskh text-[#E8D5A3] text-3xl sm:text-4xl leading-[2.2] text-center"
            >
              {arabic}
            </p>
          ) : loading ? (
            <div className="h-12 rounded bg-white/5 animate-pulse" />
          ) : null}

          <div className="h-px bg-gradient-to-r from-transparent via-gold-400/40 to-transparent" />

          {english ? (
            <p
              data-testid="translation-dialog-english"
              className="text-slate-100 text-lg sm:text-xl leading-relaxed"
            >
              {english}
            </p>
          ) : loading ? (
            <div className="space-y-2">
              <div className="h-4 rounded bg-white/5 animate-pulse" />
              <div className="h-4 rounded bg-white/5 animate-pulse w-5/6" />
              <div className="h-4 rounded bg-white/5 animate-pulse w-4/6" />
            </div>
          ) : (
            <p className="text-slate-500 text-sm italic text-center">
              Translation unavailable. Check your connection and try again.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
